import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SUZA from '../../assets/SUZA.png';
import axiosInstance from '../../api/axiosInstance';
import { useAuth } from '../../Authentication/AuthProvider';
import { setItemWithExpiry } from '../../Authentication/UseSecureLocalStorage';
import { encryptUser } from '../../Authentication/useCurrentUser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';

const mapSuzaRole = (roleStr) => {
  if (!roleStr) return 'ADMIN';
  const r = roleStr.toLowerCase();
  if (r.includes('instructor') || r.includes('teacher') || r.includes('lecturer')) return 'INSTRUCTOR';
  if (r.includes('coordinator') || r.includes('manager')) return 'MANAGER';
  return 'ADMIN';
};

const SubmitButton = ({ loading, label }) => (
  <button
    type="submit"
    className="btn w-100 py-2 fw-semibold text-white"
    disabled={loading}
    style={{ background: 'linear-gradient(135deg, #0d6efd, #00c9a7)', border: 'none', borderRadius: 8, fontSize: '1rem' }}
  >
    {loading ? (
      <span>
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        Signing in...
      </span>
    ) : label}
  </button>
);

const Login = () => {
  const [tab, setTab] = useState('staff');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/auth/staff-login', {
        userName: username,
        password,
      });
      const { accessToken, user } = response.data;
      const role = mapSuzaRole(user?.platform?.roles?.role);
      const userData = {
        id: user.userID,
        role,
        status: 'ACTIVE',
        profileImage: null,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      };
      const encrypted = encryptUser(userData);
      window.localStorage.setItem('userData', encrypted);
      window.localStorage.setItem('suzaToken', accessToken);
      login(userData);
    } catch (error) {
      console.error('Staff login failed', error);
      const msg = error.response?.data?.message || 'Login failed. Please check your credentials.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/user/login', { email, password });
      if (response.status === 200) {
        const userData = {
          id: response.data.id,
          role: response.data.role,
          status: response.data.status,
          profileImage: response.data.profileImage,
        };
        const encrypted = encryptUser(userData);
        window.localStorage.setItem('userData', encrypted);
        if (userData.role === 'STUDENT') {
          try {
            const sr = await axiosInstance.get(`/api/student/user/${userData.id}`);
            setItemWithExpiry('studentId', sr.data.student_id, 60);
          } catch (err) {
            console.error('Error fetching student data:', err);
          }
        } else if (userData.role === 'INSTRUCTOR') {
          const ir = await axiosInstance.get(`/api/instructor/user/${userData.id}`);
          setItemWithExpiry('instructorId', ir.data.instructorId, 60);
        }
        login(userData);
      }
    } catch (error) {
      console.error('Login failed', error);
      toast.error('Login failed. Please check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const isStaff = tab === 'staff';

  return (
    <div style={{ minHeight: '100vh', height: '100vh', display: 'flex', overflow: 'hidden' }}>
      {/* Branding panel */}
      <div
        className="d-none d-lg-flex flex-column align-items-center justify-content-center text-white p-5"
        style={{ width: '45%', background: 'linear-gradient(160deg, #0d6efd 0%, #0099cc 50%, #00c9a7 100%)', position: 'relative', overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', top: -80, left: -80 }} />
        <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', bottom: 40, right: -60 }} />
        <img src={SUZA} alt="SUZA Logo" style={{ height: 100, marginBottom: 32, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }} />
        <h2 className="fw-bold text-center mb-3" style={{ fontSize: '1.8rem', lineHeight: 1.3 }}>
          Swahili For<br />Foreigners System
        </h2>
        <p className="text-center opacity-75" style={{ maxWidth: 280, fontSize: '0.95rem' }}>
          Learn Swahili with expert instructors at the State University of Zanzibar.
        </p>
        <div className="mt-5 d-flex gap-3">
          {['Students', 'Instructors', 'Courses'].map((label) => (
            <div key={label} className="text-center px-3 py-2 rounded-3" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <div className="fw-semibold small">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form panel */}
      <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4" style={{ background: '#f8fafc', minHeight: '100vh', overflowY: 'auto' }}>
        <div className="d-lg-none text-center mb-4">
          <img src={SUZA} alt="SUZA Logo" style={{ height: 70 }} />
          <h5 className="fw-bold mt-2 text-dark">Swahili For Foreigners System</h5>
        </div>

        <div className="bg-white rounded-4 shadow-sm p-4 p-md-5" style={{ width: '100%', maxWidth: 420 }}>
          <h4 className="fw-bold text-dark mb-1">Welcome back</h4>
          <p className="text-muted small mb-4">Sign in to your account to continue</p>

          {/* Tabs */}
          <div className="d-flex rounded-3 mb-4 p-1" style={{ background: '#f1f5f9', gap: 4 }}>
            {[{ key: 'staff', label: 'Staff / Admin' }, { key: 'student', label: 'Student' }].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => { setTab(key); setPassword(''); setShowPassword(false); }}
                className="btn flex-fill py-2 fw-semibold small"
                style={{
                  borderRadius: 8, border: 'none',
                  background: tab === key ? '#fff' : 'transparent',
                  color: tab === key ? '#0d6efd' : '#64748b',
                  boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Staff form */}
          {isStaff && (
            <form onSubmit={handleStaffLogin}>
              <div className="mb-3">
                <label className="form-label small fw-semibold text-muted">Username / Email</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0"><FiUser className="text-muted" /></span>
                  <input
                    type="text"
                    className="form-control border-start-0 bg-light"
                    placeholder="your.username@suza.ac.tz"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label small fw-semibold text-muted">Password</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0"><FiLock className="text-muted" /></span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control border-start-0 border-end-0 bg-light"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                  <button type="button" className="input-group-text bg-light border-start-0" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FiEyeOff className="text-muted" /> : <FiEye className="text-muted" />}
                  </button>
                </div>
                <div className="mt-2">
                  <span className="badge rounded-pill small" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 500 }}>
                    SUZA Single Sign-On
                  </span>
                </div>
              </div>
              <SubmitButton loading={loading} label="Sign In with SUZA SSO" />
            </form>
          )}

          {/* Student form */}
          {!isStaff && (
            <form onSubmit={handleStudentLogin}>
              <div className="mb-3">
                <label className="form-label small fw-semibold text-muted">Email Address</label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0"><FiMail className="text-muted" /></span>
                  <input
                    type="email"
                    className="form-control border-start-0 bg-light"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <label className="form-label small fw-semibold text-muted mb-0">Password</label>
                  <Link to="/forgot-password" className="small text-decoration-none" style={{ color: '#0d6efd' }}>Forgot password?</Link>
                </div>
                <div className="input-group mt-1">
                  <span className="input-group-text bg-light border-end-0"><FiLock className="text-muted" /></span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control border-start-0 border-end-0 bg-light"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                  <button type="button" className="input-group-text bg-light border-start-0" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FiEyeOff className="text-muted" /> : <FiEye className="text-muted" />}
                  </button>
                </div>
              </div>
              <div className="mb-4">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="rememberMe" />
                  <label className="form-check-label small text-muted" htmlFor="rememberMe">Remember me</label>
                </div>
              </div>
              <SubmitButton loading={loading} label="Sign In" />
              <div className="text-center mt-4">
                <span className="text-muted small">Don't have an account? </span>
                <Link to="/register" className="small fw-semibold text-decoration-none" style={{ color: '#0d6efd' }}>Create one</Link>
              </div>
            </form>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Login;
